import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import Button from 'components/Button';
import CustomerProfile from 'components/CustomerProfile';
import Close from 'components/icons/close';
import Nexhealth from 'components/icons/nexhealth';
import User from 'components/icons/user';
import useAgentData from 'hooks/useAgentData';
import { Customer } from 'data/firestore/agent/customer/types';
import { EntityGet } from 'data/firestore/types';
import CustomerNexhealthPanel from 'customApps/nexhealth/CustomerNexhealthPanel';
import { useNexhealthPatientLink } from 'customApps/nexhealth/hooks';
import {
    Body,
    CloseButton,
    Container,
    Footer,
    StyledTab,
    StyledTabset,
    TabLabel,
    TabsetContainer,
} from './styles';

interface CustomerPanelProps {
    customer: EntityGet<Customer>;
    onRequestClose?: () => void;
}

const CustomerPanel = ({ customer, onRequestClose }: CustomerPanelProps) => {
    const { agentId = '' } = useParams();
    const navigate = useNavigate();
    const { isAppEnabled } = useAgentData();
    const [activeTab, setActiveTab] = useState('profile');
    const { data: linkResult } = useNexhealthPatientLink(
        agentId,
        customer.id,
        isAppEnabled('nexhealth'),
    );
    const [link] = linkResult?.data || [];
    const isLinked = Boolean(link && !link.stale);

    useEffect(() => {
        setActiveTab('profile');
    }, [customer.id]);

    useEffect(() => {
        if (!isLinked) setActiveTab('profile');
    }, [isLinked]);

    const handleOpenCustomer = () => {
        onRequestClose?.();
        navigate(`/${agentId}/customers/${customer.id}`);
    };

    return (
        <Container>
            <CloseButton icon={<Close />} onClick={onRequestClose} />
            <RenderIf isTrue={isLinked}>
                <TabsetContainer>
                    <StyledTabset
                        variant="line"
                        activeTabName={activeTab}
                        onSelect={(_event, selectedTab) => setActiveTab(selectedTab as string)}
                    >
                        <StyledTab
                            name="profile"
                            label={(
                                <TabLabel>
                                    <User />
                                    Profile
                                </TabLabel>
                            )}
                        />
                        <StyledTab
                            name="nexhealth"
                            label={(
                                <TabLabel>
                                    <Nexhealth />
                                    NexHealth
                                </TabLabel>
                            )}
                        />
                    </StyledTabset>
                </TabsetContainer>
            </RenderIf>
            <Body>
                <RenderIf isTrue={!isLinked || activeTab === 'profile'}>
                    <CustomerProfile agentId={agentId} customerId={customer.id} />
                </RenderIf>
                {isLinked && activeTab === 'nexhealth' && link ? (
                    <CustomerNexhealthPanel
                        agentId={agentId}
                        customerId={customer.id}
                        patientId={link.patientId}
                    />
                ) : null}
            </Body>
            <Footer>
                <Button
                    label="Open Customer"
                    variant="brand"
                    borderRadius="semi-rounded"
                    onClick={handleOpenCustomer}
                />
            </Footer>
        </Container>
    );
};

export default CustomerPanel;
